import { apiRequest } from './api';

/** Raw sell-listing fields (from /api/user/sell-listings) the estimation payload needs. */
export interface SellListingLike {
  _id: string;
  building_id?: string;
  address?: {
    street?: string;
    houseNumber?: string;
    city?: string;
    postcode?: string;
    country?: string;
    coordinates?: { lat: number; lng: number };
  };
  property_description?: string;
  property_size?: number | string;
  property_rooms?: string;
  property_floor?: number | string;
  property_has_elevator?: boolean;
  property_has_balcony?: boolean;
  property_renovation_status?: string;
  property_building_type?: string;
}

/** Number or null from an optional numeric field, preserving 0 (unlike truthiness). */
function parseOptionalNumber(value: number | string | null | undefined): number | null {
  if (value === null || value === undefined || `${value}`.trim() === '') return null;
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return null;
  return parsed;
}

/**
 * Start an AI price estimation for a sell listing
 * (POST /price-estimation/start). Progress is tracked by the existing
 * profile estimations list, not here.
 */
export async function startPriceEstimationRequest(
  listing: SellListingLike,
  language: string,
): Promise<{ estimation_id: string; status: string }> {
  const address = listing.address || {};
  return apiRequest('/price-estimation/start', {
    method: 'POST',
    body: {
      address: {
        street: address.street,
        houseNumber: address.houseNumber,
        city: address.city,
        postcode: address.postcode,
        country: address.country || 'FI',
        coordinates: address.coordinates,
      },
      property_details: {
        description: listing.property_description || '',
        size: parseOptionalNumber(listing.property_size),
        rooms: listing.property_rooms || '',
        floor: parseOptionalNumber(listing.property_floor),
        hasElevator: listing.property_has_elevator || false,
        hasBalcony: listing.property_has_balcony || false,
        renovationStatus: listing.property_renovation_status || '',
        buildingType: listing.property_building_type || '',
      },
      building_id: listing.building_id,
      willingness_id: listing._id,
      language,
      user_agent: navigator.userAgent,
    },
  });
}

/** True when the thrown apiRequest error is the backend's daily-cap 429. */
export function isDailyLimitError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { error?: string }).error === 'daily_limit_reached'
  );
}

/** True when the thrown apiRequest error is a 401 (session missing/expired). */
export function isUnauthorizedError(err: unknown): boolean {
  return (
    typeof err === 'object' &&
    err !== null &&
    (err as { status?: number }).status === 401
  );
}
